import "./ListingGroupView.less";

import { notification, Badge, Button, Icon, Popover, Spin, Tooltip } from "antd";
import { cloneDeep, maxBy, sortBy, merge } from "lodash";
import { curry, __ } from "lodash/fp";
import * as React from "react";

import { OrderedListingApi, OrderedListingGroupApi } from "lib/api";
import { CancellationToken } from "lib/api/base";
import { IBaseProps, IHighlightProps } from "lib/data/interfaces";
import { DataFieldTemplate, Listing, ListingGroup, OrderedDataFieldTemplate, OrderedListing, OrderedListingGroup, Permission, Section, SectionVersion, ListingGroupTemplate } from "lib/data/model";
import { DataFieldTemplateService, StateService } from "lib/services";

import { AuthorizedComponent, GradientBar } from "components/general";
import { useMissingTemplate } from "lib/hooks";
import { ListingGroupTable } from "./components";

interface IListingGroupViewProps extends IBaseProps, IHighlightProps {
	section: Section;
	sectionVersion: SectionVersion;
	orderedListingGroup: OrderedListingGroup;
	requiredPermission?: Permission;
	highlightWords?: string[];
	actions?: Array<React.ReactElement<Button | AuthorizedComponent<Button>>>;
	isEditable?: boolean;
}

export const ListingGroupView = (props: IListingGroupViewProps) => {

	const [addableFieldTemplates, setAddableFieldTemplates] = React.useState<OrderedDataFieldTemplate[]>([]);
	const [isLoading, setIsLoading] = React.useState<boolean>(true);
	const [isSaving, setIsSaving] = React.useState<boolean>(false);
	const [isEditable, setIsEditable] = React.useState<boolean>(false);
	const [isEditingName, setIsEditingName] = React.useState<boolean>(false);
	const [listingGroupName, setListingGroupName] = React.useState<string>("");
	const [apiCancellationToken] = React.useState<CancellationToken>(new CancellationToken());
	const [permission, setPermission] = React.useState<Permission>();

	const { component, warning, isOrphaned, setComponent } = useMissingTemplate(props.orderedListingGroup, props.section, props.sectionVersion);

	React.useEffect(() => {
		return () => {
			apiCancellationToken.cancel();
		};
	}, [apiCancellationToken]);

	React.useEffect(() => {

		const onOrderedListingGroupChange = async () => {

			const editable = props.isEditable !== undefined && props.isEditable;

			StateService.setCancelableState(() => {
				setIsLoading(true);
				setIsEditingName(false);
				setPermission(props.requiredPermission);
				setIsEditable(editable);
			}, apiCancellationToken);

			// Store Incoming Listing Group
			const clonedOrderedListingGroup = cloneDeep(component);

			// Get Rows
			const listings = await OrderedListingApi.getAll(props.section, props.sectionVersion, clonedOrderedListingGroup.item, props.isEditable);

			// Check Entries
			if (listings !== undefined) {

				// Sort Rows
				clonedOrderedListingGroup.item.listings = listings.sort(OrderedListing.sort);
			}

			// Check If Editable
			if (editable) {

				// Get Template
				const template = await OrderedListingGroupApi.getTemplate(props.section, clonedOrderedListingGroup, true);

				// Check Template
				if (template !== undefined) {

					// Set Template
					clonedOrderedListingGroup.item.template = template as ListingGroupTemplate;

					// Get Available Templates (For Columns)
					const availableTeplates = await DataFieldTemplateService.getAddableRequirements(
						clonedOrderedListingGroup.item.columns,
						clonedOrderedListingGroup.item.template.requirements
					);

					// Set As Editable
					StateService.setCancelableState(() => {
						setIsLoading(false);
						setAddableFieldTemplates(availableTeplates);
						setListingGroupName(clonedOrderedListingGroup.item.name);
					}, apiCancellationToken);
				}
				else {

					StateService.setCancelableState(() => {
						setIsLoading(false);
						setListingGroupName(clonedOrderedListingGroup.item.name);
					}, apiCancellationToken);
				}
			}
			else {

				StateService.setCancelableState(() => {
					setIsEditable(false);
					setIsLoading(false);
					setListingGroupName(clonedOrderedListingGroup.item.name);
				}, apiCancellationToken);
			}

			StateService.setCancelableState(() => setComponent(clonedOrderedListingGroup), apiCancellationToken);
		};

		onOrderedListingGroupChange();
	}, [props.isEditable, props.requiredPermission, props.section, props.sectionVersion, apiCancellationToken]);

	const onNameEditToggle = async () => {

		// Check Next State
		if (component !== undefined) {

			// Reset Name
			StateService.setCancelableState(() => {
				setListingGroupName(component.item.name);
				setIsEditingName(!isEditingName);
			}, apiCancellationToken);
		}
		else {

			// Set New Name
			StateService.setCancelableState(() => {
				setIsEditingName(!isEditingName);
			}, apiCancellationToken);
		}
	};

	const onNameSave = async () => {

		if (component !== undefined) {

			StateService.setCancelableState(() => {
				setIsSaving(true);
			}, apiCancellationToken);

			// Clone Listing Group
			const listingGroup = cloneDeep(component.item);

			// Set New Name
			listingGroup.name = listingGroupName;

			// Remove Listing Group Lists (For Saving Speed)
			delete listingGroup.columns;
			delete listingGroup.listings;

			// Save Listing Group
			await onListingGroupSave(listingGroup, false);

			// Indicate Is NOT Saving
			StateService.setCancelableState(() => {
				setIsSaving(false);
				setIsEditingName(false);
			}, apiCancellationToken);
		}
	};

	// Define Class Name
	const className = [
		"listing-group-view",
		props.className
	].filter((c) => c !== undefined).join(" ").trim();

	// Define Actions
	const actions: Array<React.ReactElement<Button | AuthorizedComponent<Button>>> = [];

	// Check Current Action State
	if (isEditingName) {

		// Set 'Save' Action
		actions.push(
			<AuthorizedComponent
				permission={permission}
				key="entry-group-save"
			>
				<Tooltip title="Save">
					<Button
						icon="check"
						type="ghost"
						loading={isSaving}
						onClick={onNameSave}
					/>
				</Tooltip>
			</AuthorizedComponent>
		);

		// Set 'Cancel' Action
		actions.push(
			<AuthorizedComponent
				permission={permission}
				key="entry-group-cancel-edit"
			>
				<Tooltip title="Cancel">
					<Button
						icon="close"
						type="ghost"
						disabled={isSaving}
						onClick={onNameEditToggle}
					/>
				</Tooltip>
			</AuthorizedComponent>
		);
	}
	else {

		// Check For Group Editability
		if (props.isEditable !== undefined && props.isEditable && isOrphaned === false) {

			// Set 'Edit' Action
			actions.push(
				<AuthorizedComponent
					permission={permission}
					key="entry-group-name-edit"
				>
					<Tooltip title="Edit">
						<Button
							icon="edit"
							type="ghost"
							onClick={onNameEditToggle}
						/>
					</Tooltip>
				</AuthorizedComponent>
			);
		}

		// Check For Provided Actions
		if (props.actions !== undefined && props.actions.length > 0) {

			// Add Provided Actions
			actions.push(...props.actions);
		}
	}

	const addListingGroupColumn = async (_event: React.MouseEvent, template: DataFieldTemplate) => {

		// Find Matching Data Field Template
		const matchingItem = addableFieldTemplates.find((item) => item.item.field.id === template.field.id);

		// Check For Matching Template
		if (matchingItem !== undefined && component !== undefined) {

			// Clone Listing Group
			const listingGroup = cloneDeep(component.item);

			// Construct New Ordered Data Field Value
			const newOrderedValue = new OrderedDataFieldTemplate();
			newOrderedValue.order = matchingItem.order;
			newOrderedValue.item = matchingItem.item;

			// Add To Values
			listingGroup.columns.push(newOrderedValue);

			// sort columns relative to the column order
			sortBy(listingGroup.columns, "order");

			// Re-Process Addable Items
			const fieldTemplates = await DataFieldTemplateService.getAddableRequirements(listingGroup.columns, listingGroup.template.requirements);

			StateService.setCancelableState(() => setAddableFieldTemplates(fieldTemplates), apiCancellationToken);

			// Save Listing Group
			await onListingGroupSave(listingGroup);
		}
	};

	const onNameChange = async (newName: string) => {

		// Set New Name
		StateService.setCancelableState(() => setListingGroupName(newName), apiCancellationToken);
	};

	const onListingGroupSave = async (listingGroup: ListingGroup, showLoading: boolean = true) => {


		if (showLoading) {

			// Indicate Is Loading
			StateService.setCancelableState(() => setIsLoading(true), apiCancellationToken);
		}

		if (component !== undefined) {

			// Clone Ordered Listing Group
			const clonedOrderedListingGroup = cloneDeep(component);

			// Add Listing Group To Ordered Listing Group
			clonedOrderedListingGroup.item = listingGroup;

			// Save Listing Group
			const savedListingGroup = await OrderedListingGroupApi.save(props.section, clonedOrderedListingGroup);

			// Check Saved Listing Group
			if (savedListingGroup !== undefined) {

				// Re-Process Addable Items
				const fieldTemplates = await DataFieldTemplateService.getAddableRequirements(savedListingGroup.item.columns, savedListingGroup.item.template.requirements);

				// Set Current Listing Group
				StateService.setCancelableState(() => setComponent(savedListingGroup), apiCancellationToken);

				if (showLoading) {

					// Indicate Is NOT Loading
					StateService.setCancelableState(() => {
						setAddableFieldTemplates(fieldTemplates);
						setIsLoading(false);
					}, apiCancellationToken);
				}
				else {

					// Update Addable Templates
					StateService.setCancelableState(() => setAddableFieldTemplates(fieldTemplates), apiCancellationToken);
				}

				notification.success({
					message: "Update Successful",
					description: `${savedListingGroup.item.name} was sucessfully saved`
				});
			}
			else {

				if (showLoading) {

					// Indicate Is NOT Loading
					StateService.setCancelableState(() => setIsLoading(false), apiCancellationToken);
				}

				notification.error({
					message: "Update Failed",
					description: `${clonedOrderedListingGroup.item.name} failed to save`
				});
			}
		}
	};

	const addNewListing = async () => {

		if (component !== undefined) {

			// Calculate Max Order In Entries
			const highestOrderEntry = maxBy(component.item.listings, (i) => i.order);

			// Construct New Listing
			const listing = new Listing();
			listing.name = "New Item";

			// Construct Ordered Entry
			const orderedListing = new OrderedListing();
			orderedListing.order = (highestOrderEntry ? highestOrderEntry.order + 1 : 0);
			orderedListing.item = listing;

			const clonedOrderedListingGroup = cloneDeep(component);

			// Add Imported Entry To List
			clonedOrderedListingGroup.item.listings.push(orderedListing);

			// setComponent(clonedOrderedListingGroup);

			StateService.setCancelableState(() => {
				setIsLoading(true);
			}, apiCancellationToken);

			const savedListingGroup = await OrderedListingGroupApi.save(props.section, clonedOrderedListingGroup);

			if (savedListingGroup !== undefined) {

				StateService.setCancelableState(() => {
					setComponent(savedListingGroup);
				}, apiCancellationToken);

				notification.success({
					message: "Created a new listing"
				});
			}
			else {

				notification.error({
					message: "Failed to create a new listing"
				});
			}

			StateService.setCancelableState(() => {
				setIsLoading(false);
			}, apiCancellationToken);
		}
	};

	const onListingGroupChanged = (listingGroup: ListingGroup) => {

		// Set Updated Entry Group
		if (component !== undefined) {

			const clonedEntryGroup = cloneDeep(component);
			Object.assign(clonedEntryGroup.item, listingGroup);

			StateService.setCancelableState(() => setComponent(clonedEntryGroup), apiCancellationToken);
		}
	};

	return (

		<div className={className}>

			{
				component !== undefined
					?
					(

						<GradientBar
							className="listing-group-view-bar"
							childrenClassName="listing-group-view-bar-children"
							size="medium"
							title={component.item.name}
							highlightWords={props.highlightWords}
							isEditing={isEditingName}
							onNameChange={onNameChange}
							actions={actions}
						>

							<section className="listing-group-view-content">
								<div className="warning-container">
									{
										isOrphaned && props.isEditable === true
											?
											warning
											:
											undefined
									}
								</div>
								<div className="table-container">
									<Spin spinning={isLoading}>
										<ListingGroupTable
											className={isEditable ? "listings-table listings-table-editable" : "listings-table"}
											section={props.section}
											listingGroup={component.item}
											requiredPermission={permission}
											highlightWords={props.highlightWords}
											isEditable={isEditable}
											onSave={onListingGroupSave}
											onComplete={onListingGroupChanged}
											footer={props.isEditable && isOrphaned === false
												?
												(
													<AuthorizedComponent permission={props.requiredPermission}>
														<Button.Group className="table-footer-buttons">
															<Button
																icon="plus"
																size="large"
																onClick={addNewListing}
															>
																Create
															</Button>
														</Button.Group>
													</AuthorizedComponent>
												)
												:
												undefined
											}
										/>
									</Spin>
									{
										!isLoading && props.isEditable && addableFieldTemplates.length > 0
											?
											(
												<Popover
													placement="left"
													trigger="click"
													title="Add a Column"
													content={
														addableFieldTemplates.map((template) => {

															// Check if field is required
															let isRequired = false;
															const requirement = DataFieldTemplateService.getRequirementFromTemplate(component.item.template.requirements, template.item);

															if (requirement !== undefined) {

																isRequired = !DataFieldTemplateService.isRequirementSatisfied(requirement, component.item.columns.map((i) => i.item));
															}

															return (
																<div key={template.item.field.id} className="add-listing-group-column-button-container">
																	<Button
																		className={`add-listing-group-column-button${isRequired ? " required-item" : ""}`}
																		onClick={curry(addListingGroupColumn)(__, template.item)}
																		icon="plus-circle"
																	>
																		{
																			isRequired
																				?
																				(
																					<Badge offset={[17, 0]} count={<div className="listing-group-view-required-addable-field-menu-item">required</div>}>
																						{template.item.field.name}
																					</Badge>
																				)
																				:
																				template.item.field.name
																		}
																	</Button>
																</div>
															);
														})
													}
												>
													<Button className="listing-group-add-column-button">
														Add<br />Column<br />
														<Icon className="column-add-icon" type="plus-circle" />
													</Button>
												</Popover>
											)
											:
											null
									}
								</div>
							</section>
						</GradientBar>
					)
					:
					null
			}
		</div>
	);
};
